import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  Factory,
  Scale,
  AlertCircle,
  Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import SEO, { regionSchema } from "@/components/SEO";

const STATUS_TAG = {
  advancing: "bb-tag-positive",
  permitting: "bb-tag-neutral",
  delayed: "bb-tag-negative",
  operational: "bb-tag-positive",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const Panel = ({ icon: Icon, overline, title, body, testId }) => (
  <div
    className="bg-white border border-border p-6 lg:p-7 flex flex-col gap-3"
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div className="bb-overline text-bb-copper">{overline}</div>
      <Icon size={20} strokeWidth={1.6} className="text-bb-navy" />
    </div>
    <h3 className="font-archivo text-lg text-bb-navy font-semibold tracking-tight">
      {title}
    </h3>
    <p className="text-sm text-bb-slate leading-relaxed">{body}</p>
  </div>
);

export default function RegionDetailPage() {
  const { slug } = useParams();
  const [region, setRegion] = useState(null);
  const [projects, setProjects] = useState([]);
  const [articles, setArticles] = useState([]);
  const [policyItems, setPolicyItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    setRegion(null);
    setProjects([]);
    setArticles([]);
    setPolicyItems([]);

    api
      .get(`/regions/${slug}`)
      .then(async (r) => {
        if (!mounted) return;
        const region_ = r.data;
        setRegion(region_);
        const [projRes, artRes, polRes] = await Promise.all([
          api.get("/projects"),
          api.get("/articles"),
          api.get("/policy-items"),
        ]);
        if (!mounted) return;
        setProjects(
          (Array.isArray(projRes.data) ? projRes.data : []).filter(
            (p) => p.region === region_.id,
          ),
        );
        setArticles(
          (Array.isArray(artRes.data) ? artRes.data : [])
            .filter((a) => a.region === region_.id)
            .slice(0, 6),
        );
        setPolicyItems(
          (Array.isArray(polRes.data) ? polRes.data : [])
            .filter((p) => p.relatedRegion === region_.id)
            .slice(0, 6),
        );
      })
      .catch(() => mounted && setError(true))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div
        className="bb-container py-20 text-center bb-overline text-bb-slate"
        data-testid="region-loading"
      >
        Loading region…
      </div>
    );
  }

  if (error || !region) {
    return (
      <section className="bb-container py-20" data-testid="region-not-found">
        <div className="border border-border bg-white p-12 text-center max-w-2xl mx-auto">
          <AlertCircle className="text-bb-copper mx-auto" size={28} />
          <div className="bb-overline text-bb-copper mt-4">404 · Region</div>
          <h1 className="font-archivo text-3xl text-bb-navy mt-3 font-bold">
            Region not found.
          </h1>
          <Link
            to="/regions"
            className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
            data-testid="region-back-to-list"
          >
            <ArrowLeft size={14} /> Back to regions
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article data-testid={`region-detail-${region.slug}`}>
      <SEO
        title={`${region.name} — Canadian Mining`}
        description={region.summary}
        type="article"
        jsonLd={regionSchema(region)}
      />
      {/* Hero */}
      <header className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <Link
            to="/regions"
            className="bb-link inline-flex items-center gap-2 text-xs font-archivo uppercase tracking-widest text-bb-navy mb-8"
            data-testid="region-back-link"
          >
            <ArrowLeft size={14} /> Back to Regions
          </Link>

          <div className="bb-overline text-bb-copper">
            Jurisdiction · Canada
          </div>
          <h1
            className="bb-headline mt-4 text-4xl sm:text-5xl lg:text-6xl text-bb-navy"
            data-testid="region-title"
          >
            {region.name}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-bb-slate leading-relaxed max-w-3xl">
            {region.summary}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {(region.keyCommodities || []).map((c) => (
              <span
                key={c}
                className="bb-tag-neutral text-[11px] font-mono px-3 py-1.5"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* Panels */}
      <section className="py-12 lg:py-16">
        <div className="bb-container">
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            data-testid="region-panels"
          >
            <Panel
              icon={Factory}
              overline="Key Commodities"
              title={(region.keyCommodities || []).slice(0, 3).join(", ")}
              body={`${(region.keyCommodities || []).length} commodities actively tracked in this jurisdiction. The province's mix reflects its geological endowment and downstream industry linkages.`}
              testId="region-panel-commodities"
            />
            <Panel
              icon={MapPin}
              overline="Infrastructure"
              title="Access, power &amp; logistics"
              body={region.infrastructureNotes}
              testId="region-panel-infrastructure"
            />
            <Panel
              icon={Scale}
              overline="Policy Context"
              title="Regulatory &amp; Indigenous framework"
              body={region.policyContext}
              testId="region-panel-policy"
            />
          </div>
        </div>
      </section>

      {/* Projects */}
      <section
        className="py-12 lg:py-16 border-t border-border bg-white"
        data-testid="region-projects-section"
      >
        <div className="bb-container">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="bb-overline text-bb-copper">
                Project pipeline
              </div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 font-bold tracking-tight">
                Active projects in {region.name}
              </h2>
            </div>
            <Link
              to={`/projects?region=${region.id}`}
              className="bb-link text-xs font-archivo uppercase tracking-widest text-bb-navy inline-flex items-center gap-1"
              data-testid="region-projects-link"
            >
              View all in database
              <ArrowUpRight size={14} strokeWidth={2.4} />
            </Link>
          </div>

          {projects.length === 0 ? (
            <div className="border border-dashed border-border bg-bb-offwhite p-8 text-sm text-bb-slate font-mono">
              No projects currently tracked in this jurisdiction.
            </div>
          ) : (
            <div className="bg-white border border-border overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border bg-bb-offwhite">
                    <th className="p-4 bb-overline text-bb-slate">Project</th>
                    <th className="p-4 bb-overline text-bb-slate">Commodity</th>
                    <th className="p-4 bb-overline text-bb-slate">Stage</th>
                    <th className="p-4 bb-overline text-bb-slate">Status</th>
                    <th className="p-4 bb-overline text-bb-slate">Capex</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-border last:border-b-0 hover:bg-bb-offwhite transition-colors"
                      data-testid={`region-project-${p.slug}`}
                    >
                      <td className="p-4 align-top">
                        <div className="font-archivo font-semibold text-bb-navy">
                          <Link to={`/projects/${p.slug}`} className="bb-link">
                            {p.name}
                          </Link>
                        </div>
                      </td>
                      <td className="p-4 align-top text-xs font-mono text-bb-slate">
                        {(p.commodity || []).join(" · ")}
                      </td>
                      <td className="p-4 align-top bb-overline text-bb-navy">
                        {p.stage}
                      </td>
                      <td className="p-4 align-top">
                        <span
                          className={`text-[11px] font-mono px-2 py-1 ${STATUS_TAG[p.status] || "bb-tag-neutral"}`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="p-4 align-top font-mono text-sm text-bb-navy">
                        {p.capexEstimate
                          ? `$${p.capexEstimate.toLocaleString()}M`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Coverage + policy split */}
      <section
        className="py-14 lg:py-20 border-t border-border"
        data-testid="region-coverage-section"
      >
        <div className="bb-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="bb-overline text-bb-copper">Coverage</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 mb-6 font-bold tracking-tight">
                Recent dispatches
              </h2>
              {articles.length === 0 ? (
                <div className="border border-dashed border-border bg-white p-6 text-sm text-bb-slate font-mono">
                  No dispatches on the desk for this region yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {articles.map((a) => (
                    <Link
                      key={a.id}
                      to={`/news/${a.slug}`}
                      className="bb-card p-5 flex flex-col gap-2 group"
                      data-testid={`region-article-${a.slug}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="bb-overline text-bb-copper">
                          {a.category}
                        </span>
                        <span className="text-[11px] font-mono text-bb-slate">
                          {formatDate(a.publishedDate)}
                        </span>
                      </div>
                      <h3 className="font-archivo text-lg text-bb-navy font-semibold tracking-tight leading-snug">
                        <span className="bb-link">{a.title}</span>
                      </h3>
                      <div className="flex items-center gap-2 text-[11px] font-mono text-bb-slate mt-1">
                        <Clock size={11} /> By {a.author}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-5">
              <div className="bb-overline text-bb-copper">Policy</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 mb-6 font-bold tracking-tight">
                Active policy files
              </h2>
              {policyItems.length === 0 ? (
                <div className="border border-dashed border-border bg-white p-6 text-sm text-bb-slate font-mono">
                  No active policy items on file for this region.
                </div>
              ) : (
                <div className="bg-white border border-border">
                  {policyItems.map((p, i) => (
                    <div
                      key={p.id}
                      className={`p-5 flex flex-col gap-1.5 ${
                        i !== policyItems.length - 1
                          ? "border-b border-border"
                          : ""
                      }`}
                      data-testid={`region-policy-${p.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="bb-overline text-bb-copper">
                          {p.jurisdiction}
                        </span>
                        <span className="text-[10px] font-mono text-bb-slate">
                          {formatDate(p.effectiveDate)}
                        </span>
                      </div>
                      <div className="font-archivo font-semibold text-bb-navy text-sm leading-tight">
                        {p.title}
                      </div>
                      <div className="text-xs text-bb-slate font-mono">
                        {p.agency} · {p.status}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
